/**
 * Super Administrator — Team Leader Leaderboard (/super-admin/leaderboard)
 *
 * Cosmetic-but-functional ranking view over `GET /api/staff-monitoring/leaderboard/team-leaders`.
 * Every figure here already exists elsewhere in the system (messages, exams, certificates, CRM,
 * sessions) — this page's only job is to bring them together per Team Leader and rank them, not to
 * compute anything new client-side.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchLeaderboardWeights,
  fetchTeamLeaderLeaderboard,
  updateLeaderboardWeights,
  type LeaderboardWeights,
  type TeamLeaderLeaderboardRow,
} from "@/lib/api/staff-monitoring";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableSkeleton } from "@/components/loading";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Award,
  ClipboardCheck,
  Clock,
  Loader2,
  Medal,
  MessageSquare,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Trophy,
  Users,
  Zap,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/super-admin/leaderboard")({
  component: LeaderboardPage,
});

const DEFAULT_WEIGHTS: LeaderboardWeights = {
  hours: 25,
  studentsSupervised: 15,
  messagesSent: 15,
  examsReviewed: 10,
  assignmentsReviewed: 10,
  certificatesIssued: 10,
  crmActions: 10,
  responsiveness: 5,
};

const METRIC_FIELDS: Array<{
  key: keyof LeaderboardWeights;
  label: string;
  id: string;
}> = [
  { key: "hours", label: "Hours", id: "weight-hours" },
  { key: "studentsSupervised", label: "Students supervised", id: "weight-studentsSupervised" },
  { key: "messagesSent", label: "Messages", id: "weight-messagesSent" },
  { key: "examsReviewed", label: "Exams reviewed", id: "weight-examsReviewed" },
  { key: "assignmentsReviewed", label: "Assignments reviewed", id: "weight-assignmentsReviewed" },
  { key: "certificatesIssued", label: "Certificates issued", id: "weight-certificatesIssued" },
  { key: "crmActions", label: "CRM actions", id: "weight-crmActions" },
  { key: "responsiveness", label: "Responsiveness", id: "weight-responsiveness" },
];

type Window = "today" | "week" | "month";

function hours(value: number): string {
  return `${value.toFixed(1)}h`;
}

function scoreColor(score: number): string {
  if (score >= 75) return "text-green-600";
  if (score >= 40) return "text-amber-600";
  return "text-destructive";
}

function scoreBar(score: number): string {
  if (score >= 75) return "[&>div]:bg-green-500";
  if (score >= 40) return "[&>div]:bg-amber-500";
  return "[&>div]:bg-destructive";
}

/** Rank 1-3 get a medal; colours match `AchievementsLeaderboard`'s gold/silver/bronze. */
const RANK_MEDAL = [
  { className: "text-amber-500", label: "1st place" },
  { className: "text-slate-400", label: "2nd place" },
  { className: "text-orange-700", label: "3rd place" },
];

/**
 * Start of the selected window, in UTC.
 *
 * The server buckets on calendar boundaries computed in UTC
 * (`GetTeamLeaderLeaderboardAsync`: `StartOfDay(now)`, `todayStart.AddDays(-(int)DayOfWeek)` for a
 * Sunday-based week, and the 1st for the month). Mirror those exactly, or the windowed metrics
 * would disagree with the Hours column, which reads the server's pre-computed buckets.
 *
 * `toUtc` is deliberately not sent — the server defaults it to UtcNow, and sending a per-render
 * timestamp would change the query key on every render and refetch forever.
 */
function windowStartUtc(key: Window): string {
  const now = new Date();
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (key === "today") return new Date(todayStart).toISOString();
  if (key === "week") {
    const dayOfWeek = new Date(todayStart).getUTCDay(); // 0 = Sunday, matching the server
    return new Date(todayStart - dayOfWeek * 86_400_000).toISOString();
  }
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

export function LeaderboardPage() {
  const { isSuperAdmin, loading } = useAuth();
  const [windowKey, setWindowKey] = useState<Window>("week");
  const queryClient = useQueryClient();

  // The selector used to change nothing but which pre-computed hours column was displayed: the
  // request carried no window at all, so the server fell back to its 30-day default and every
  // other metric (messages, exams, certs, CRM, response time) — and therefore the score and the
  // ranking — stayed on that 30-day window regardless of the selection.
  const fromUtc = useMemo(() => windowStartUtc(windowKey), [windowKey]);

  const leaderboard = useQuery({
    queryKey: ["staff-monitoring", "leaderboard", windowKey],
    queryFn: () => fetchTeamLeaderLeaderboard({ fromUtc }),
    enabled: isSuperAdmin,
    refetchInterval: 60_000,
  });

  const weightsQuery = useQuery({
    queryKey: ["leaderboard-weights"],
    queryFn: fetchLeaderboardWeights,
    enabled: isSuperAdmin,
  });

  const [isWeightsDialogOpen, setIsWeightsDialogOpen] = useState(false);
  const [formWeights, setFormWeights] = useState<Record<keyof LeaderboardWeights, string>>({
    hours: "",
    studentsSupervised: "",
    messagesSent: "",
    examsReviewed: "",
    assignmentsReviewed: "",
    certificatesIssued: "",
    crmActions: "",
    responsiveness: "",
  });

  const openWeightsDialog = () => {
    if (weightsQuery.data) {
      setFormWeights({
        hours: String(weightsQuery.data.hours),
        studentsSupervised: String(weightsQuery.data.studentsSupervised),
        messagesSent: String(weightsQuery.data.messagesSent),
        examsReviewed: String(weightsQuery.data.examsReviewed),
        assignmentsReviewed: String(weightsQuery.data.assignmentsReviewed),
        certificatesIssued: String(weightsQuery.data.certificatesIssued),
        crmActions: String(weightsQuery.data.crmActions),
        responsiveness: String(weightsQuery.data.responsiveness),
      });
    } else {
      setFormWeights({
        hours: "",
        studentsSupervised: "",
        messagesSent: "",
        examsReviewed: "",
        assignmentsReviewed: "",
        certificatesIssued: "",
        crmActions: "",
        responsiveness: "",
      });
    }
    setIsWeightsDialogOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openWeightsDialog();
    } else {
      setIsWeightsDialogOpen(false);
    }
  };

  const handleResetDefaults = () => {
    setFormWeights({
      hours: String(DEFAULT_WEIGHTS.hours),
      studentsSupervised: String(DEFAULT_WEIGHTS.studentsSupervised),
      messagesSent: String(DEFAULT_WEIGHTS.messagesSent),
      examsReviewed: String(DEFAULT_WEIGHTS.examsReviewed),
      assignmentsReviewed: String(DEFAULT_WEIGHTS.assignmentsReviewed),
      certificatesIssued: String(DEFAULT_WEIGHTS.certificatesIssued),
      crmActions: String(DEFAULT_WEIGHTS.crmActions),
      responsiveness: String(DEFAULT_WEIGHTS.responsiveness),
    });
  };

  const total = useMemo(() => {
    return METRIC_FIELDS.reduce((sum, { key }) => {
      const raw = formWeights[key];
      const val = parseFloat(raw);
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  }, [formWeights]);

  const isTotalValid = useMemo(() => {
    return Math.abs(total - 100) < 0.01;
  }, [total]);

  const isAnyFieldInvalid = useMemo(() => {
    return METRIC_FIELDS.some(({ key }) => {
      const raw = formWeights[key];
      if (raw === undefined || raw === null || raw.trim() === "") return true;
      const val = Number(raw);
      return Number.isNaN(val) || val < 0;
    });
  }, [formWeights]);

  const updateWeightsMutation = useMutation({
    mutationFn: updateLeaderboardWeights,
    onSuccess: () => {
      toast.success("Leaderboard weights updated successfully.");
      queryClient.invalidateQueries({ queryKey: ["leaderboard-weights"] });
      queryClient.invalidateQueries({ queryKey: ["staff-monitoring", "leaderboard"] });
      setIsWeightsDialogOpen(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update weights.");
    },
  });

  const canSave = !isAnyFieldInvalid && isTotalValid && !updateWeightsMutation.isPending;

  const handleSave = () => {
    if (!canSave) return;
    const payload: LeaderboardWeights = {
      hours: Number(formWeights.hours),
      studentsSupervised: Number(formWeights.studentsSupervised),
      messagesSent: Number(formWeights.messagesSent),
      examsReviewed: Number(formWeights.examsReviewed),
      assignmentsReviewed: Number(formWeights.assignmentsReviewed),
      certificatesIssued: Number(formWeights.certificatesIssued),
      crmActions: Number(formWeights.crmActions),
      responsiveness: Number(formWeights.responsiveness),
    };
    updateWeightsMutation.mutate(payload);
  };

  const descriptionSentence = useMemo(() => {
    if (weightsQuery.isLoading) return null;
    if (!weightsQuery.data) {
      return "Score weighs activity metrics relative to the cohort, capped for extended absence and zero for a Team Leader who has never signed in.";
    }
    const w = weightsQuery.data;
    return `Score weighs hours (${w.hours}%), students supervised (${w.studentsSupervised}%), messages (${w.messagesSent}%), exams reviewed (${w.examsReviewed}%), assignments reviewed (${w.assignmentsReviewed}%), certificates issued (${w.certificatesIssued}%), CRM actions (${w.crmActions}%) and responsiveness (${w.responsiveness}%) relative to the cohort, capped for extended absence and zero for a Team Leader who has never signed in.`;
  }, [weightsQuery.isLoading, weightsQuery.data]);

  const formattedTotal = Number.isInteger(total) ? String(total) : String(Number(total.toFixed(2)));

  const rows = useMemo(() => {
    const items = leaderboard.data?.rows ?? [];
    // Score is server-computed and already the primary sort key from the API; re-sorting here just
    // guards against a future API change in ordering rather than duplicating the scoring logic.
    return [...items].sort((a, b) => b.activityScore - a.activityScore);
  }, [leaderboard.data]);

  const hoursFor = (row: TeamLeaderLeaderboardRow) =>
    windowKey === "today"
      ? row.hoursToday
      : windowKey === "week"
        ? row.hoursThisWeek
        : row.hoursThisMonth;

  if (loading) return null;
  if (!isSuperAdmin) {
    return (
      <AccessDenied message="The Team Leader leaderboard is limited to the Super Administrator." />
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Trophy className="h-7 w-7 text-primary" />
            Team Leader Leaderboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every Team Leader ranked by an activity score out of 100 — hours worked, students
            supervised, responsiveness, and mentoring output.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Disabled until the weights have loaded: the dialog seeds its fields from the fetched
              values, so opening it early would present eight empty boxes and a total of 0 — and the
              user would have to retype the whole set to get Save enabled. A failed query is NOT
              disabled: that dialog opens deliberately, to say the weights could not be read. */}
          <Button
            variant="outline"
            size="sm"
            onClick={openWeightsDialog}
            disabled={weightsQuery.isLoading}
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Edit weights
          </Button>
          <Select value={windowKey} onValueChange={(v) => setWindowKey(v as Window)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ranking</CardTitle>
          <CardDescription className="text-xs">
            {weightsQuery.isLoading ? (
              <span className="text-muted-foreground">Loading weighting criteria…</span>
            ) : (
              descriptionSentence
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {leaderboard.isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={6} columns={7} />
            </div>
          ) : leaderboard.isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                {(leaderboard.error as Error)?.message ?? "Could not load the leaderboard."}
              </p>
              <Button variant="outline" size="sm" onClick={() => leaderboard.refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No Team Leaders found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3 w-14">Rank</th>
                    <th className="text-left font-semibold px-4 py-3">Team Leader</th>
                    <th className="text-right font-semibold px-4 py-3">Hours</th>
                    <th className="text-right font-semibold px-4 py-3">Students</th>
                    <th className="text-right font-semibold px-4 py-3">Messages</th>
                    <th className="text-right font-semibold px-4 py-3">Exams</th>
                    <th className="text-right font-semibold px-4 py-3">Assignments</th>
                    <th className="text-right font-semibold px-4 py-3">Certs</th>
                    <th className="text-right font-semibold px-4 py-3">CRM</th>
                    <th className="text-right font-semibold px-4 py-3">Avg response</th>
                    <th className="text-right font-semibold px-4 py-3">Absence</th>
                    <th className="text-left font-semibold px-4 py-3 w-44">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row, i) => (
                    <tr key={row.userId} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 font-semibold tabular-nums">
                          {RANK_MEDAL[i] ? (
                            <Medal
                              className={`h-4 w-4 ${RANK_MEDAL[i].className}`}
                              aria-label={RANK_MEDAL[i].label}
                            />
                          ) : (
                            `#${i + 1}`
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{row.fullName || row.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                          {row.department && (
                            <p className="text-[11px] text-muted-foreground/70">{row.department}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          {hours(hoursFor(row))}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                          {row.studentsSupervised}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                          {row.messagesSent}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                          {row.examsReviewed}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-right tabular-nums"
                        title="Assignment attempts graded or returned for revision"
                      >
                        <span className="inline-flex items-center gap-1 justify-end">
                          <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground" />
                          {row.assignmentsReviewed}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <Award className="h-3.5 w-3.5 text-muted-foreground" />
                          {row.certificatesIssued}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.crmActions}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {row.averageResponseMinutes == null
                          ? "—"
                          : row.averageResponseMinutes < 60
                            ? `${Math.round(row.averageResponseMinutes)}m`
                            : `${(row.averageResponseMinutes / 60).toFixed(1)}h`}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.daysWithoutLogin < 0 ? (
                          <Badge variant="destructive" className="text-[10px]">
                            never signed in
                          </Badge>
                        ) : row.daysWithoutLogin === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={
                              row.daysWithoutLogin >= 7 ? "text-destructive font-medium" : ""
                            }
                          >
                            {row.daysWithoutLogin}d
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={row.activityScore}
                            className={`h-2 w-24 ${scoreBar(row.activityScore)}`}
                          />
                          <span
                            className={`text-xs font-semibold tabular-nums ${scoreColor(row.activityScore)}`}
                          >
                            {row.activityScore}
                          </span>
                          {i === 0 && row.activityScore > 0 && (
                            <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {leaderboard.data && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5" />
          Generated {new Date(leaderboard.data.generatedAtUtc).toLocaleString()} · window{" "}
          {new Date(leaderboard.data.windowFromUtc).toLocaleDateString()} –{" "}
          {new Date(leaderboard.data.windowToUtc).toLocaleDateString()}
        </p>
      )}

      <Dialog open={isWeightsDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Leaderboard Weights</DialogTitle>
            <DialogDescription>
              Configure the percentage weight for each activity metric. All weights must total
              exactly 100%.
            </DialogDescription>
          </DialogHeader>

          {weightsQuery.isError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
              Failed to load current weights:{" "}
              {(weightsQuery.error as Error)?.message || "Server error"}
            </div>
          )}

          <div
            className={cn(
              "flex items-center justify-between rounded-md p-3 border text-sm",
              isTotalValid
                ? "bg-muted/40 border-border text-foreground"
                : "bg-destructive/10 border-destructive/30 text-destructive",
            )}
          >
            <span className="font-medium">Weight distribution</span>
            <span
              className={cn(
                "tabular-nums",
                isTotalValid ? "font-semibold" : "font-bold text-destructive",
              )}
            >
              Total: {formattedTotal} / 100
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {METRIC_FIELDS.map(({ key, label, id }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={id}>{label}</Label>
                <Input
                  id={id}
                  type="number"
                  min={0}
                  max={100}
                  value={formWeights[key]}
                  onChange={(e) =>
                    setFormWeights((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  disabled={updateWeightsMutation.isPending}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="sm:justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={handleResetDefaults}
              disabled={updateWeightsMutation.isPending}
            >
              Reset to defaults
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsWeightsDialogOpen(false)}
                disabled={updateWeightsMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={!canSave}>
                {updateWeightsMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {updateWeightsMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
